import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Brain, Download, FileText, Heart, BookOpen, Shield, IndianRupee, QrCode, CheckCircle2 } from 'lucide-react';
import { freeResources, paidResources } from '../mockData';
import { toast } from 'sonner';
import { purchaseAPI } from '../services/api';

const iconMap = {
  FileText,
  Heart,
  BookOpen,
  Shield
};

const Resources = () => {
  const [selectedResource, setSelectedResource] = useState(null);
  const [showPaymentDialog, setShowPaymentDialog] = useState(false);
  const [currentPurchaseId, setCurrentPurchaseId] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [buyerInfo, setBuyerInfo] = useState({ name: '', email: '' });

  const handleFreeDownload = (resource) => {
    toast.success(`Downloading ${resource.title}...`);
    // In real implementation, this would trigger actual download
  };

  const handlePurchase = (resource) => {
    setSelectedResource(resource);
    setShowPaymentDialog(true);
    setBuyerInfo({ name: '', email: '' });
    setCurrentPurchaseId(null);
  };

  const handleInitiatePurchase = async () => {
    if (!buyerInfo.name || !buyerInfo.email) {
      toast.error('Please enter your name and email');
      return;
    }

    setIsSubmitting(true);
    
    try {
      const purchaseData = {
        resourceId: selectedResource.id,
        resourceTitle: selectedResource.title,
        resourcePrice: selectedResource.price,
        buyerEmail: buyerInfo.email,
        buyerName: buyerInfo.name
      };
      
      const response = await purchaseAPI.create(purchaseData);
      
      if (response.success) {
        setCurrentPurchaseId(response.purchase._id);
        toast.success('Purchase initiated! Please complete payment.');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create purchase');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handlePaymentComplete = async () => {
    if (!currentPurchaseId) {
      await handleInitiatePurchase();
      return;
    }

    setIsSubmitting(true);
    
    try {
      const response = await purchaseAPI.updatePayment(currentPurchaseId, {
        paymentStatus: 'completed',
        paymentId: `UPI_${Date.now()}`
      });
      
      if (response.success) {
        toast.success(`Purchase successful! Download link sent to ${buyerInfo.email}.`);
        setShowPaymentDialog(false);
        setSelectedResource(null);
        setCurrentPurchaseId(null);
        setBuyerInfo({ name: '', email: '' });
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to confirm payment');
    } finally {
      setIsSubmitting(false);
    }
  };

  const generateUPILink = () => {
    const amount = selectedResource?.price || 0;
    const upiId = 'rainbowayesha-2@oksbi';
    const name = 'Ayesha Khan';
    const description = `${selectedResource?.title}`;
    
    return `upi://pay?pa=${upiId}&pn=${encodeURIComponent(name)}&am=${amount}&cu=INR&tn=${encodeURIComponent(description)}`;
  };

  return (
    <div className="min-h-screen">
      {/* Header */}
      <header className="sticky top-0 z-50 w-full border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
        <div className="container mx-auto px-4 flex h-20 items-center justify-between">
          <Link to="/" className="flex items-center">
            <img
              src="https://customer-assets.emergentagent.com/job_wellness-journeys/artifacts/amcb4t0p_Generated_image%20%281%29.png"
              alt="Therapy with Ayesha"
              className="h-16 w-auto"
            />
          </Link>
          <nav className="hidden md:flex items-center gap-6">
            <Link to="/" className="text-sm font-medium hover:text-primary">Home</Link>
            <Link to="/services" className="text-sm font-medium hover:text-primary">Services</Link>
            <Link to="/resources" className="text-sm font-medium text-primary">Healing Resources</Link>
            <Link to="/#contact" className="text-sm font-medium hover:text-primary">Contact</Link>
          </nav>
        </div>
      </header>

      <div className="container mx-auto px-4 py-12">
        <div className="max-w-6xl mx-auto space-y-16">
          {/* Header */}
          <div className="text-center space-y-4">
            <h1 className="text-4xl md:text-5xl font-serif font-bold text-foreground">
              Healing Resources
            </h1>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Evidence-based tools and psychoeducational resources to support your mental health journey
            </p>
          </div>

          {/* All Resources - Free + Paid in one grid */}
          <section className="space-y-6">
            <div className="grid md:grid-cols-2 gap-6">
              {[...freeResources.map(r => ({...r, isFree: true})), ...paidResources.map(r => ({...r, isFree: false}))].map((resource) => {
                const Icon = iconMap[resource.icon];
                return (
                  <Card key={resource.id} className={`card-hover ${resource.isFree ? '' : 'border-primary/20'}`}>
                    <CardHeader>
                      <div className="flex items-start justify-between">
                        <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${resource.isFree ? 'bg-secondary/30' : 'bg-primary/10'}`}>
                          <Icon className="w-6 h-6 text-primary" />
                        </div>
                        {resource.isFree ? (
                          <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200">
                            Free
                          </Badge>
                        ) : (
                          <div className="flex items-center gap-1 text-primary font-bold text-xl">
                            <IndianRupee className="w-5 h-5" />
                            {resource.price}
                          </div>
                        )}
                      </div>
                      <CardTitle className="text-xl font-serif mt-4">{resource.title}</CardTitle>
                      <Badge variant="secondary" className="w-fit text-xs">{resource.type}</Badge>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <CardDescription className="text-base leading-relaxed">
                        {resource.description}
                      </CardDescription>
                      {!resource.isFree && resource.features && (
                        <div className="space-y-2">
                          <p className="text-sm font-semibold">What's Included:</p>
                          <ul className="space-y-1">
                            {resource.features.map((feature, index) => (
                              <li key={index} className="flex items-center gap-2 text-sm text-muted-foreground">
                                <CheckCircle2 className="w-4 h-4 text-primary flex-shrink-0" />
                                <span>{feature}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </CardContent>
                    <CardFooter>
                      {resource.isFree ? (
                        <Button
                          onClick={() => handleFreeDownload(resource)}
                          className="w-full bg-primary hover:bg-primary/90"
                          data-testid={`download-free-${resource.id}`}
                        >
                          <Download className="mr-2 h-4 w-4" />
                          Download Now
                        </Button>
                      ) : (
                        <Button
                          onClick={() => handlePurchase(resource)}
                          className="w-full bg-primary hover:bg-primary/90"
                          data-testid={`purchase-${resource.id}`}
                        >
                          Purchase & Download
                        </Button>
                      )}
                    </CardFooter>
                  </Card>
                );
              })}
            </div>
          </section>

          {/* CTA to Intake Form */}
          <section className="bg-card rounded-2xl p-8 border-2 border-primary/20">
            <div className="max-w-3xl mx-auto text-center space-y-6">
              <h2 className="text-3xl font-serif font-bold text-foreground">
                Ready to Start Therapy?
              </h2>
              <p className="text-muted-foreground">
                Complete our confidential intake form to help us prepare for a meaningful first session.
              </p>
              <Link to="/intake">
                <Button size="lg" className="bg-primary hover:bg-primary/90" data-testid="go-to-intake-btn">
                  Fill Out Intake Form
                </Button>
              </Link>
            </div>
          </section>
        </div>
      </div>

      {/* Payment Dialog */}
      <Dialog open={showPaymentDialog} onOpenChange={setShowPaymentDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-2xl font-serif">Complete Payment</DialogTitle>
            <DialogDescription>
              Pay ₹{selectedResource?.price} to purchase {selectedResource?.title}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-6 py-4">
            {!currentPurchaseId && (
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="buyerName">Your Name *</Label>
                  <Input 
                    id="buyerName"
                    value={buyerInfo.name}
                    onChange={(e) => setBuyerInfo({...buyerInfo, name: e.target.value})}
                    placeholder="Enter your full name"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="buyerEmail">Your Email *</Label>
                  <Input 
                    id="buyerEmail"
                    type="email"
                    value={buyerInfo.email}
                    onChange={(e) => setBuyerInfo({...buyerInfo, email: e.target.value})}
                    placeholder="Enter your email"
                  />
                </div>
              </div>
            )}

            <div className="bg-muted p-6 rounded-lg text-center space-y-4">
              <img
                src="https://customer-assets.emergentagent.com/job_wellness-journeys/artifacts/a5zjbopz_WhatsApp%20Image%202026-05-18%20at%209.41.09%20PM.jpeg"
                alt="UPI QR Code — Ayesha Khan"
                className="w-56 h-56 mx-auto rounded-lg shadow-md object-cover"
                data-testid="resources-upi-qr"
              />
              <p className="text-sm text-muted-foreground">Scan to pay with any UPI app</p>
              <div className="bg-background p-4 rounded border">
                <p className="text-xs text-muted-foreground mb-1">UPI ID</p>
                <p className="font-mono font-semibold">rainbowayesha-2@oksbi</p>
              </div>
            </div>

            <div className="text-center">
              <p className="text-sm text-muted-foreground mb-2">Or pay using</p>
              <a 
                href={generateUPILink()} 
                className="inline-block w-full"
              >
                <Button variant="outline" className="w-full">
                  Open UPI App
                </Button>
              </a>
            </div>

            <div className="space-y-2">
              <Button 
                onClick={handlePaymentComplete} 
                className="w-full bg-primary hover:bg-primary/90"
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Processing...' : "I've Completed Payment"}
              </Button>
              <p className="text-xs text-center text-muted-foreground">
                Download link will be sent to your email after verification
              </p>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Resources;